import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Search, ArrowLeft, Plus, Trash2, Pencil, BookOpen, X as XIcon, ArrowUp, ArrowDown } from 'lucide-react';
import { apiService } from '../../../services/apiService';
import type { InvoiceCatalogItem, InvoiceLanguage } from '../../../services/apiService';
import { useToast } from '../../../contexts/ToastContext';
import PageHeader from '../ui/PageHeader';
import StatusPill from '../ui/StatusPill';
import EmptyState from '../ui/EmptyState';
import { fmtMoneyPlain, parseMoneyInput } from './invoiceHelpers';

const inputClass =
    'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';
const labelClass = 'block text-[11px] font-medium uppercase tracking-wider text-brand-text-muted mb-1';

const CATEGORY_KEYS: Record<string, string> = {
    implantology: 'billing.category.implantology',
    prophylaxis: 'billing.category.prophylaxis',
    aesthetic: 'billing.category.aesthetic',
    oral_surgery: 'billing.category.oral_surgery',
    orthodontics: 'billing.category.orthodontics',
    consultation: 'billing.category.consultation',
    other: 'billing.category.other',
};

const blankItem = (): Partial<InvoiceCatalogItem> => ({
    code: '',
    name: { de: '', es: '', en: '' },
    description: { de: '', es: '', en: '' },
    default_unit: 'Stk',
    default_price_cents: 0,
    default_vat_rate: 21,
    category: 'other',
    is_active: true,
    sort_order: 0,
});

const CatalogPage: React.FC = () => {
    const { t } = useTranslation();
    const { addToast } = useToast();
    const [items, setItems] = useState<InvoiceCatalogItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [category, setCategory] = useState('');
    const [activeOnly, setActiveOnly] = useState(true);
    const [editor, setEditor] = useState<Partial<InvoiceCatalogItem> | null>(null);

    const load = async () => {
        try {
            setLoading(true);
            const res = await apiService.listInvoiceCatalog({ activeOnly: false });
            setItems(res.data.data || []);
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('billing.catalog.loadError'), 'error');
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return items.filter(it => {
            if (activeOnly && !it.is_active) return false;
            if (category && it.category !== category) return false;
            if (!q) return true;
            const hay = [
                it.code,
                it.name?.de, it.name?.es, it.name?.en,
                it.description?.de, it.description?.es, it.description?.en,
            ].filter(Boolean).join(' ').toLowerCase();
            return hay.includes(q);
        });
    }, [items, query, category, activeOnly]);

    const hasActiveFilters = !!(query || category || !activeOnly);
    const resetFilters = () => { setQuery(''); setCategory(''); setActiveOnly(true); };

    const saveItem = async () => {
        if (!editor) return;
        try {
            if (editor.id) {
                await apiService.updateInvoiceCatalogItem(editor.id, editor);
                addToast(t('billing.catalog.itemUpdated'), 'success');
            } else {
                await apiService.createInvoiceCatalogItem(editor);
                addToast(t('billing.catalog.itemCreated'), 'success');
            }
            setEditor(null);
            await load();
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('billing.saveFailed'), 'error');
        }
    };

    const removeItem = async (it: InvoiceCatalogItem) => {
        const name = it.name.de || it.name.es || it.name.en || it.code || '?';
        if (!window.confirm(t('billing.catalog.confirmDelete', { name }))) return;
        try {
            await apiService.deleteInvoiceCatalogItem(it.id);
            addToast(t('billing.catalog.itemDeleted'), 'success');
            await load();
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('billing.deleteFailed'), 'error');
        }
    };

    const moveSort = async (it: InvoiceCatalogItem, delta: number) => {
        try {
            await apiService.updateInvoiceCatalogItem(it.id, { sort_order: (it.sort_order || 0) + delta });
            await load();
        } catch (e: any) {
            addToast(e?.response?.data?.message || t('billing.sortFailed'), 'error');
        }
    };

    return (
        <div>
            <PageHeader
                eyebrow={t('billing.eyebrow')}
                title={t('billing.catalog.title')}
                description={t('billing.catalog.description')}
                actions={
                    <>
                        <Link
                            to="/admin/invoices"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-text-muted border border-brand-border hover:bg-brand-surface-2 rounded-md transition-colors cursor-pointer"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
                            {t('billing.catalog.backToInvoices')}
                        </Link>
                        <button
                            type="button"
                            onClick={() => setEditor(blankItem())}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors cursor-pointer"
                        >
                            <Plus className="h-3.5 w-3.5" strokeWidth={2} />
                            {t('billing.catalog.newItem')}
                        </button>
                    </>
                }
            />

            {/* Filter card */}
            <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-4 mb-4">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-1 relative">
                        <label className={labelClass}>{t('billing.catalog.searchLabel')}</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-text-muted pointer-events-none" strokeWidth={1.75} aria-hidden />
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder={t('billing.catalog.searchPlaceholder')}
                                className={`${inputClass} pl-9`}
                            />
                        </div>
                    </div>
                    <div>
                        <label className={labelClass}>{t('billing.catalog.categoryLabel')}</label>
                        <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass}>
                            <option value="">{t('billing.catalog.categoryAll')}</option>
                            {Object.entries(CATEGORY_KEYS).map(([k, v]) => (
                                <option key={k} value={k}>{t(v)}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>{t('billing.catalog.visibilityLabel')}</label>
                        <select value={activeOnly ? 'active' : 'all'} onChange={(e) => setActiveOnly(e.target.value === 'active')} className={inputClass}>
                            <option value="active">{t('billing.catalog.visibilityActive')}</option>
                            <option value="all">{t('billing.catalog.visibilityAll')}</option>
                        </select>
                    </div>
                </div>
                {hasActiveFilters && (
                    <div className="mt-3 flex justify-end">
                        <button onClick={resetFilters} className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:text-brand-primary-hover transition-colors cursor-pointer">
                            <XIcon className="h-3 w-3" strokeWidth={2} />
                            {t('billing.filterReset')}
                        </button>
                    </div>
                )}
            </div>

            {/* List */}
            <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-sm text-brand-text-muted">{t('billing.loading')}</div>
                ) : filtered.length === 0 ? (
                    <EmptyState
                        icon={BookOpen}
                        title={hasActiveFilters ? t('billing.catalog.emptyFilterTitle') : t('billing.catalog.emptyTitle')}
                        description={hasActiveFilters
                            ? t('billing.catalog.emptyFilterDescription')
                            : t('billing.catalog.emptyDescription')}
                        action={
                            !hasActiveFilters ? (
                                <button
                                    type="button"
                                    onClick={() => setEditor(blankItem())}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md cursor-pointer"
                                >
                                    <Plus className="h-3.5 w-3.5" strokeWidth={2} />
                                    {t('billing.catalog.emptyAction')}
                                </button>
                            ) : undefined
                        }
                    />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-brand-border">
                            <thead className="bg-brand-surface-2/60">
                                <tr className="text-[11px] font-semibold uppercase tracking-wider text-brand-text-muted">
                                    <th className="px-4 py-2.5 text-left w-16">{t('billing.catalog.colSort')}</th>
                                    <th className="px-4 py-2.5 text-left">{t('billing.catalog.colName')}</th>
                                    <th className="px-4 py-2.5 text-left">{t('billing.catalog.colCodeCategory')}</th>
                                    <th className="px-4 py-2.5 text-right">{t('billing.catalog.colPriceVat')}</th>
                                    <th className="px-4 py-2.5 text-left">{t('billing.catalog.colVisible')}</th>
                                    <th className="px-4 py-2.5 text-right">{t('billing.catalog.colAction')}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-brand-border">
                                {filtered.map((it, idx) => (
                                    <tr key={it.id} className="hover:bg-brand-surface-2/30">
                                        <td className="px-4 py-3 text-sm">
                                            <div className="flex items-center gap-0.5">
                                                <button onClick={() => moveSort(it, -10)} className="p-1 text-brand-text-muted hover:text-brand-text rounded cursor-pointer" aria-label={t('billing.catalog.moveUp')} title="Sort−"><ArrowUp className="h-3.5 w-3.5" strokeWidth={1.75} /></button>
                                                <span className="text-xs text-brand-text-muted tabular-nums w-6 text-center">{it.sort_order || 0}</span>
                                                <button onClick={() => moveSort(it, 10)} className="p-1 text-brand-text-muted hover:text-brand-text rounded cursor-pointer" aria-label={t('billing.catalog.moveDown')} title="Sort+"><ArrowDown className="h-3.5 w-3.5" strokeWidth={1.75} /></button>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            <div className="font-medium text-brand-text">
                                                {it.name.de || it.name.es || it.name.en || <span className="italic text-brand-text-muted">{t('billing.catalog.unnamed')}</span>}
                                            </div>
                                            {(it.description.de || it.description.es) && (
                                                <div className="text-xs text-brand-text-muted truncate max-w-md">
                                                    {it.description.de || it.description.es || it.description.en}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            <div className="text-brand-text tabular-nums font-mono text-xs">{it.code || '—'}</div>
                                            <div className="text-xs text-brand-text-muted">{CATEGORY_KEYS[it.category] ? t(CATEGORY_KEYS[it.category]) : it.category}</div>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right">
                                            <div className="font-medium text-brand-text tabular-nums">{fmtMoneyPlain(it.default_price_cents)} € / {it.default_unit}</div>
                                            <div className="text-xs text-brand-text-muted tabular-nums">{t('billing.catalog.vatShort', { rate: it.default_vat_rate })}</div>
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            <StatusPill tone={it.is_active ? 'success' : 'neutral'} dot>
                                                {it.is_active ? t('billing.catalog.active') : t('billing.catalog.inactive')}
                                            </StatusPill>
                                        </td>
                                        <td className="px-4 py-3 text-sm text-right">
                                            <div className="inline-flex items-center gap-1">
                                                <button onClick={() => setEditor({ ...it })} className="p-1.5 text-brand-primary hover:bg-brand-accent/10 rounded cursor-pointer" aria-label={t('common.edit')} title={t('common.edit')}>
                                                    <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                                                </button>
                                                <button onClick={() => removeItem(it)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded cursor-pointer" aria-label={t('common.delete')} title={t('common.delete')}>
                                                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Editor modal */}
            {editor && (
                <CatalogItemEditor
                    item={editor}
                    onChange={setEditor}
                    onSave={saveItem}
                    onClose={() => setEditor(null)}
                />
            )}
        </div>
    );
};

// --------------------------------------------------------------------
// Inline editor modal
// --------------------------------------------------------------------

interface EditorProps {
    item: Partial<InvoiceCatalogItem>;
    onChange: (next: Partial<InvoiceCatalogItem>) => void;
    onSave: () => void;
    onClose: () => void;
}

const CatalogItemEditor: React.FC<EditorProps> = ({ item, onChange, onSave, onClose }) => {
    const { t } = useTranslation();
    const setName = (lang: InvoiceLanguage, value: string) =>
        onChange({ ...item, name: { ...(item.name || { de: '', es: '', en: '' }), [lang]: value } });
    const setDescription = (lang: InvoiceLanguage, value: string) =>
        onChange({ ...item, description: { ...(item.description || { de: '', es: '', en: '' }), [lang]: value } });

    const [priceText, setPriceText] = useState(fmtMoneyPlain(item.default_price_cents || 0));
    useEffect(() => { setPriceText(fmtMoneyPlain(item.default_price_cents || 0)); }, [item.default_price_cents]);

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-12 px-4 bg-black/40" onClick={onClose}>
            <div className="w-full max-w-2xl rounded-xl bg-brand-surface border border-brand-border shadow-xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-brand-border">
                    <h3 className="text-sm font-semibold text-brand-text">
                        {item.id ? t('billing.catalog.editorEditTitle') : t('billing.catalog.editorNewTitle')}
                    </h3>
                    <button onClick={onClose} className="p-1 text-brand-text-muted hover:text-brand-text cursor-pointer" aria-label={t('common.close')}>
                        <XIcon className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    {/* Code + category + active */}
                    <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                        <div className="sm:col-span-2">
                            <label className={labelClass}>{t('billing.catalog.codeLabel')}</label>
                            <input type="text" value={item.code || ''} onChange={(e) => onChange({ ...item, code: e.target.value })} className={inputClass} placeholder={t('billing.catalog.codePlaceholder')} maxLength={32} />
                        </div>
                        <div className="sm:col-span-2">
                            <label className={labelClass}>{t('billing.catalog.categoryLabel')}</label>
                            <select value={item.category || 'other'} onChange={(e) => onChange({ ...item, category: e.target.value as any })} className={inputClass}>
                                {Object.entries(CATEGORY_KEYS).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
                            </select>
                        </div>
                        <div className="sm:col-span-2">
                            <label className={labelClass}>{t('billing.catalog.statusLabel')}</label>
                            <select value={item.is_active === false ? 'false' : 'true'} onChange={(e) => onChange({ ...item, is_active: e.target.value === 'true' })} className={inputClass}>
                                <option value="true">{t('billing.catalog.statusActive')}</option>
                                <option value="false">{t('billing.catalog.statusInactive')}</option>
                            </select>
                        </div>
                    </div>

                    {/* Names per language */}
                    <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-text mb-2">{t('billing.catalog.nameSection')}</h4>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                            {(['de', 'es', 'en'] as InvoiceLanguage[]).map(l => (
                                <div key={l}>
                                    <label className={labelClass}>{l.toUpperCase()}</label>
                                    <input type="text" value={item.name?.[l] || ''} onChange={(e) => setName(l, e.target.value)} className={inputClass} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Descriptions */}
                    <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-brand-text mb-2">{t('billing.catalog.descriptionSection')}</h4>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                            {(['de', 'es', 'en'] as InvoiceLanguage[]).map(l => (
                                <div key={l}>
                                    <label className={labelClass}>{l.toUpperCase()}</label>
                                    <textarea rows={2} value={item.description?.[l] || ''} onChange={(e) => setDescription(l, e.target.value)} className={inputClass} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Pricing */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div>
                            <label className={labelClass}>{t('billing.catalog.unitLabel')}</label>
                            <input type="text" value={item.default_unit || 'Stk'} onChange={(e) => onChange({ ...item, default_unit: e.target.value })} className={inputClass} />
                        </div>
                        <div>
                            <label className={labelClass}>{t('billing.catalog.priceLabel')}</label>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={priceText}
                                onChange={(e) => setPriceText(e.target.value)}
                                onBlur={() => onChange({ ...item, default_price_cents: parseMoneyInput(priceText) })}
                                className={inputClass}
                                placeholder="0,00"
                            />
                        </div>
                        <div>
                            <label className={labelClass}>{t('billing.catalog.vatLabel')}</label>
                            <input type="number" min={0} max={100} value={item.default_vat_rate ?? 21} onChange={(e) => onChange({ ...item, default_vat_rate: Number(e.target.value) })} className={inputClass} />
                        </div>
                        <div>
                            <label className={labelClass}>{t('billing.catalog.sortOrderLabel')}</label>
                            <input type="number" value={item.sort_order ?? 0} onChange={(e) => onChange({ ...item, sort_order: Number(e.target.value) })} className={inputClass} />
                        </div>
                    </div>
                </div>

                <div className="px-5 py-3 border-t border-brand-border flex justify-end gap-2">
                    <button onClick={onClose} className="px-3 py-1.5 text-xs font-medium text-brand-text-muted hover:bg-brand-surface-2 rounded cursor-pointer">
                        {t('common.cancel')}
                    </button>
                    <button onClick={onSave} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md cursor-pointer">
                        {item.id ? t('common.save') : t('billing.create')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CatalogPage;
